import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../services/check_login';
import htmlPopupTemplates from '../../html-versions/html-popup';
import htmlEmailTemplates from '../../html-versions/html-email';
import { generateHTMLFromTemplate } from '../../html-versions/email-template-generator';
import { IoArrowBack } from "react-icons/io5";
import { FaDownload, FaEdit, FaCopy, FaCloudUploadAlt } from "react-icons/fa";
import { motion, AnimatePresence } from 'framer-motion';
import '../../../assets/css/creativePreview.css'
import '../../../assets/css/integrations.css'

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Helper функции за броене на символи и думи
const calculateSMSStats = (text) => {
  if (!text) return { totalCount: 0, totalSMS: 0, gsm: 0, unicode: 0, nextPart: 160 };
  
  const textLength = text.length;
  const gsmChars = /^[A-Za-z0-9@£$¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,\-./:;<=>?¡ÄÖÑÜ§¿äöñüà^{}\\[\]\|~€]*$/;
  const isUnicode = !gsmChars.test(text);
  
  let smsLimit = isUnicode ? 70 : 160;
  let totalSMS = Math.ceil(textLength / smsLimit);
  let nextPart = smsLimit - (textLength % smsLimit);
  
  if (nextPart === smsLimit) nextPart = 0;
  
  return {
    totalCount: textLength,
    totalSMS: totalSMS,
    gsm: isUnicode ? 0 : textLength,
    unicode: isUnicode ? textLength : 0,
    nextPart: nextPart
  };
};

const calculateTextStats = (text) => {
  if (!text) return { characters: 0, words: 0 };
  
  // Strip HTML tags for accurate counting
  const plainText = text.replace(/<[^>]*>/g, '');
  const words = plainText.trim().split(/\s+/).filter(word => word.length > 0).length;
  
  return {
    characters: plainText.length,
    words: words
  };
};

const ViewCreative = () => {
  const { lang, id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [creativeData, setCreativeData] = useState(null);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [copied, setCopied] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState('version1');
  const [customTemplates, setCustomTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Remember selected brand and version across language changes
  const [rememberedBrandId, setRememberedBrandId] = useState(null);
  const [rememberedVersion, setRememberedVersion] = useState('version1');

  // Global permissions
  const permissions = currentUser?.role?.permissions || {};
  const canCopyHTML = permissions.canCopyHTML || currentUser?.isAdmin;
  const canExtractContent = permissions.canExtractContent || currentUser?.isAdmin;
  const canPushToCRM = permissions.canPushToCRM || currentUser?.isAdmin;

  // CRM integration (e.g. Optimove)
  const [activeCrm, setActiveCrm] = useState(null);
  const [showPushModal, setShowPushModal] = useState(false);
  const [crmLink, setCrmLink] = useState(null);
  const [crmTemplates, setCrmTemplates] = useState([]);
  const [pushForm, setPushForm] = useState({ templateName: '', subject: '', target: 'linked', externalId: '' });
  // Provider-specific push fields (e.g. Optimove brand)
  const [pushOptions, setPushOptions] = useState({});
  // Fields matched automatically by the Content Craft brand name - no choice shown for them
  const [autoPushFields, setAutoPushFields] = useState({});
  const [refreshingCrmOptions, setRefreshingCrmOptions] = useState(false);
  // Options of fields that depend on another field (e.g. Optimove folders of the chosen brand)
  const [dependentOptions, setDependentOptions] = useState({});
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  // Push every language at once
  const [pushAllLanguages, setPushAllLanguages] = useState(false);
  const [subjectKey, setSubjectKey] = useState('subjectLine1');
  const [languageLinks, setLanguageLinks] = useState([]);
  const [languageResults, setLanguageResults] = useState({});

  // Permission checks based on creative type
  const getCreativePermissions = (creativeType) => {
    const type = creativeType?.toLowerCase();
    const permissions = currentUser?.role?.permissions || {};
    
    switch(type) {
      case 'sms':
        return {
          canEdit: permissions.canEditSMS || currentUser?.isAdmin,
        };
      case 'email':
        return {
          canEdit: permissions.canEditEmail || currentUser?.isAdmin,
        };
      case 'popup':
        return {
          canEdit: permissions.canEditPopup || currentUser?.isAdmin,
        };
      case 'push-notification':
        return {
          canEdit: permissions.canEditPushNotification || currentUser?.isAdmin,
        };
      default:
        return { canEdit: false };
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // First fetch creative to know the type
        const creativeResponse = await api.get(`/view/${lang}/${id}`);
        
        if (!creativeResponse.data) {
          throw new Error('Creative not found');
        }

        setCreativeData(creativeResponse.data);

        const fetchPromises = [
          api.get('/brand/list')
        ];

        // Fetch custom templates only for emails
        if (creativeResponse.data.creative === 'Email') {
      
          try {
            const templatesRes = await api.get('/api/email-templates/list');
            setCustomTemplates(templatesRes.data || []);
          } catch (templatesError) {
            console.error('Error fetching templates:', templatesError);
            console.error('Error response:', templatesError.response);
          }
        }

        const responses = await Promise.all(fetchPromises);
        const brandsResponse = responses[0];

        setBrands(brandsResponse.data);

        if (brandsResponse.data.length > 0) {
          // If we have a remembered brand, try to find and select it
          if (rememberedBrandId) {
            const rememberedBrand = brandsResponse.data.find(b => b._id === rememberedBrandId);
            if (rememberedBrand) {
              setSelectedBrand(rememberedBrand);
            } else {
              // Brand not found, use first
              setSelectedBrand(brandsResponse.data[0]);
              setRememberedBrandId(brandsResponse.data[0]._id);
            }
          } else {
            // First load, select first brand
            setSelectedBrand(brandsResponse.data[0]);
            setRememberedBrandId(brandsResponse.data[0]._id);
          }
          
          // Restore remembered version
          if (rememberedVersion) {
            setSelectedVersion(rememberedVersion);
          }
        }
      } catch (err) {
        console.error('Error:', err);
        setError(err.response?.data?.error || err.message || 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    if (lang && id) {
      fetchData();
    }
  }, [lang, id, navigate]);

  const handleLanguageChange = (newLang) => {
    navigate(`/view/${newLang}/${id}`);
  };

  const handleBrandChange = (brand) => {
    setSelectedBrand(brand);
    setRememberedBrandId(brand._id); // Remember the brand
  };

  const handleVersionChange = (version) => {
    setSelectedVersion(version);
    setRememberedVersion(version); // Remember the version
  };

  const getLogoLinkForLanguage = (lang) => {
    if (!selectedBrand) return '';
    const langData = selectedBrand.langs.find((l) => l.languageName === lang);
    return langData ? langData.logoLink : '';
  };

  const handleExtract = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await api.get(`/extract/view/${id}`);
      const creativeData = response.data;

      if (!creativeData || !creativeData.content) {
        console.log('No data available');
        return;
      }

      const generateHTML = () => {
        let wordContent = `
        <!DOCTYPE html>
        <html lang="en">
        <head>
            <meta charset="UTF-8">
            <meta http-equiv="X-UA-Compatible" content="IE=edge">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Creative Data</title>
        </head>
        <body>
            <div class="container extractText">
              <h2>${creativeData.title}</h2>
        `;

        creativeData.content.forEach((langData) => {
          wordContent += `
            <div>
                <h3>${langData.language}</h3>
          `;

          if (langData.headline) {
            wordContent += `<p><b>Title: </b>${langData.headline}</p>`;
          }

          if (langData.body) {
            wordContent += `<p><b>Body: </b>${langData.body}</p>`;
          }

          wordContent += `
            </div>
            <hr />
          `;
        });

        wordContent += `
            </div>
        </body>
        </html>
        `;

        return wordContent;
      };

      const wordContent = generateHTML();
      const blob = new Blob([wordContent], { type: "application/msword" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${creativeData.title || "creative"}.doc`;
      link.click();
    } catch (error) {
      console.error('Error fetching new data:', error.response || error.message);
    }
  };

  const handleEdit = () => {
    navigate(`/creative/edit/${lang}/${id}`);
  };

  // HTML for the selected brand + template version, for any language's content
  const buildHTML = (content, language) => getTemplateVersion(
      selectedVersion,
      language,
      creativeData.title || '',
      content.headline || '',
      content.body || '',
      content.bodyBtnName || '',
      content.bodyBtnLink || '',
      content.subjectLine1 || '',
      content.subjectLine2 || '',
      content.subjectLine3 || '',
      content.preHeader || '',
      content.bodyImage || '',
      content.bodyImageLink || '',
      content.additionalText || '',
      content.additionalTextUnderBTN || '',
      selectedBrand.brandName,
      selectedBrand.disclaimer_marketing,
      selectedBrand.viewInBrowserURL,
      selectedBrand.frameColor,
      getLogoLinkForLanguage(language),
      selectedBrand.backgroundColor,
      selectedBrand.buttonGenralColor,
      selectedBrand.buttonTextColor,
      selectedBrand.logo,
      selectedBrand.smalldisclaimer,
      selectedBrand.borderRadius
    );

  const buildCurrentHTML = () => buildHTML(creativeData.content, lang);

  const handleCopyHTML = () => {
    if (!creativeData || !creativeData.content || !selectedBrand) return;

    const htmlString = buildCurrentHTML();

    navigator.clipboard
      .writeText(htmlString)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => console.error('Error copying to clipboard:', err));
  };

  // Load the active CRM once we know the creative is an email
  useEffect(() => {
    if (creativeData?.creative !== 'Email' || !canPushToCRM) return;
    api.get('/api/integrations/active')
      .then(res => setActiveCrm(res.data))
      .catch(err => console.error('Error fetching active CRM:', err));
  }, [creativeData?.creative, canPushToCRM]);

  const subjectOptions = creativeData?.content
    ? ['subjectLine1', 'subjectLine2', 'subjectLine3']
        .map(key => creativeData.content[key])
        .filter(Boolean)
    : [];

  // Reloads the CRM dropdown data (e.g. Optimove brands) and returns the updated active CRM
  const refreshCrmOptions = async () => {
    setRefreshingCrmOptions(true);
    try {
      const res = await api.post('/api/integrations/templates/refresh-options');
      setActiveCrm(res.data);
      return res.data;
    } catch (err) {
      console.error('Error refreshing CRM options:', err);
      return activeCrm;
    } finally {
      setRefreshingCrmOptions(false);
    }
  };

  // Matches dropdown fields to the Content Craft brand by name, otherwise keeps a saved/only option
  // "Warren Bowie & Smith" -> "warrenbowiesmith"
  const normalizeName = (name) => (name || '').toLowerCase().replace(/[^a-z0-9]/g, '');

  const getNameMatches = (field) => field.options.filter(o => normalizeName(o.label) === normalizeName(selectedBrand?.brandName));

  // Several options can share a name (e.g. a brand and its QA copy) - prefer the clearly heaviest one
  const pickMainOption = (options) => {
    if (options.length <= 1) return options[0];
    const sorted = [...options].sort((a, b) => (b.weight || 0) - (a.weight || 0));
    return (sorted[0].weight || 0) > (sorted[1].weight || 0) ? sorted[0] : undefined;
  };

  // Options shown in a dropdown: one per name (the main one), plus the selected value
  const getVisibleOptions = (field, selectedValue) => {
    const groups = new Map();
    field.options.forEach(o => {
      const key = normalizeName(o.label);
      groups.set(key, [...(groups.get(key) || []), o]);
    });
    const visible = new Set();
    groups.forEach(group => {
      const main = pickMainOption(group);
      (main ? [main] : group).forEach(o => visible.add(o.value));
    });
    if (selectedValue) visible.add(selectedValue);
    return field.options.filter(o => visible.has(o.value));
  };

  // Dropdown fields are checked on every push: the option named like the Content Craft brand
  // (the main one if several share the name) is used automatically, anything else is left to choose.
  const applyPushDefaults = (crm, savedOptions) => {
    const initialOptions = { ...savedOptions };
    const autoFields = {};
    crm.pushFields.forEach(field => {
      if (!field.options) return;
      const main = pickMainOption(getNameMatches(field));
      if (main) {
        initialOptions[field.key] = main.value;
        autoFields[field.key] = { ...main, reason: 'matched by brand name' };
      } else {
        initialOptions[field.key] = field.options.length === 1 ? field.options[0].value : '';
      }
    });
    setPushOptions(initialOptions);
    setAutoPushFields(autoFields);
  };

  // Load dependent field options whenever the field they depend on changes
  const dependencyKey = (activeCrm?.pushFields || [])
    .filter(f => f.dependsOn)
    .map(f => `${f.key}:${pushOptions[f.dependsOn] || ''}`)
    .join('|');

  useEffect(() => {
    if (!showPushModal || !activeCrm) return;
    let cancelled = false;
    activeCrm.pushFields.filter(f => f.dependsOn).forEach(async (field) => {
      const parentValue = pushOptions[field.dependsOn];
      if (!parentValue) {
        setDependentOptions(prev => ({ ...prev, [field.key]: [] }));
        return;
      }
      setDependentOptions(prev => ({ ...prev, [field.key]: null }));
      try {
        const res = await api.get('/api/integrations/templates/field-options', {
          params: { field: field.key, [field.dependsOn]: parentValue }
        });
        if (cancelled) return;
        const options = res.data || [];
        setDependentOptions(prev => ({ ...prev, [field.key]: options }));
        // Drop a saved value that does not exist for the new parent (e.g. folder of another brand)
        setPushOptions(prev => (prev[field.key] && !options.some(o => o.value === prev[field.key])
          ? { ...prev, [field.key]: '' }
          : prev));
      } catch (err) {
        if (!cancelled) setDependentOptions(prev => ({ ...prev, [field.key]: [] }));
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPushModal, dependencyKey]);

  const handleRefreshCrmOptions = async () => {
    const crm = await refreshCrmOptions();
    if (crm) applyPushDefaults(crm, pushOptions);
  };

  const openPushModal = async () => {
    if (!selectedBrand) return;
    setPushResult(null);
    setCrmTemplates([]);
    setLanguageResults({});
    setLanguageLinks([]);
    setPushAllLanguages(false);
    api.get('/api/integrations/templates/links', { params: { creativeId: id, brandId: selectedBrand._id } })
      .then(res => setLanguageLinks(res.data || []))
      .catch(err => console.error('Error fetching CRM template links:', err));

    let link = null;
    let lastOptions = {};
    try {
      const res = await api.get('/api/integrations/templates/link', {
        params: { creativeId: id, language: lang, brandId: selectedBrand._id }
      });
      link = res.data?.link || null;
      lastOptions = res.data?.lastOptions || {};
    } catch (err) {
      console.error('Error fetching CRM template link:', err);
    }

    // An option named like the Content Craft brand is used automatically.
    // Otherwise prefill with the options of this template / the last push for the same brand.
    // Dropdowns with no choices yet are reloaded from the CRM first.
    const savedOptions = link?.options || lastOptions;
    const needsRefresh = activeCrm.pushFields.some(f => f.options && f.options.length === 0);
    applyPushDefaults(activeCrm, savedOptions);
    if (needsRefresh) {
      refreshCrmOptions().then(crm => crm && applyPushDefaults(crm, savedOptions));
    }

    setCrmLink(link);
    setPushForm({
      templateName: link?.templateName || `${creativeData.title || 'Creative'} - ${lang.toUpperCase()} - ${selectedBrand.brandName}`,
      subject: link?.subject || subjectOptions[0] || '',
      target: 'linked',
      externalId: ''
    });
    setShowPushModal(true);
  };

  const setPushOption = (key, value) => {
    setPushOptions(prev => ({ ...prev, [key]: value }));
  };

  const renderPushField = (field) => {
    const value = pushOptions[field.key] ?? '';
    const autoMatch = autoPushFields[field.key];

    if (field.dependsOn) {
      const options = dependentOptions[field.key];
      const loading = options === null;
      return (
        <select
          className="integration-input"
          value={value}
          disabled={loading || !pushOptions[field.dependsOn]}
          onChange={(e) => setPushOption(field.key, e.target.value)}
        >
          <option value="">{loading ? 'Loading...' : `— ${field.emptyLabel || 'Not set'} —`}</option>
          {(options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }

    if (autoMatch) {
      return (
        <div className="crm-push-auto">
          <span>{autoMatch.label} (ID {autoMatch.value}){autoMatch.hint ? ` · ${autoMatch.hint}` : ''}</span>
          <small>{autoMatch.reason}</small>
          <button
            type="button"
            className="crm-push-change"
            onClick={() => setAutoPushFields(prev => ({ ...prev, [field.key]: undefined }))}
          >
            Change
          </button>
        </div>
      );
    }

    if (field.options) {
      return (
        <>
          <div className="crm-push-select-row">
            <select
              className="integration-input"
              value={value}
              disabled={refreshingCrmOptions}
              onChange={(e) => setPushOption(field.key, e.target.value)}
            >
              <option value="">{refreshingCrmOptions ? 'Loading...' : '— Select —'}</option>
              {getVisibleOptions(field, value).map(o => (
                <option key={o.value} value={o.value}>{o.label} (ID {o.value})</option>
              ))}
            </select>
            <button
              type="button"
              className="integration-btn icon-only"
              onClick={handleRefreshCrmOptions}
              disabled={refreshingCrmOptions}
              title={`Reload from ${activeCrm.name}`}
            >
              ↻
            </button>
          </div>
          {!refreshingCrmOptions && (
            <small className={field.options.length === 0 ? 'integration-error' : 'integration-help'}>
              {field.options.length === 0
                ? (activeCrm.syncError || `No brands found in ${activeCrm.name}.`)
                : getNameMatches(field).length > 1
                  ? `Several ${activeCrm.name} brands are named "${selectedBrand?.brandName}" — select where to save it.`
                  : `"${selectedBrand?.brandName}" was not found in ${activeCrm.name} — select where to save it.`}
            </small>
          )}
        </>
      );
    }

    return (
      <input
        type={field.type === 'number' ? 'number' : 'text'}
        className="integration-input"
        value={value}
        onChange={(e) => setPushOption(field.key, e.target.value)}
      />
    );
  };

  const missingPushFields = (activeCrm?.pushFields || []).filter(f => f.required && !pushOptions[f.key]);

  const handlePushTargetChange = async (target) => {
    setPushForm(prev => ({ ...prev, target }));
    if (target === 'existing' && crmTemplates.length === 0) {
      try {
        const res = await api.get('/api/integrations/templates/list');
        setCrmTemplates(res.data || []);
      } catch (err) {
        setPushResult({ type: 'error', message: err.response?.data?.error || 'Failed to load templates' });
      }
    }
  };

  const allLanguagesBaseName = `${creativeData?.title || 'Creative'} - ${selectedBrand?.brandName || ''}`;

  // Pushes one template per language with the same CRM options.
  // Runs sequentially so a failing language does not stop the others and rate limits are respected.
  const handlePushAllLanguages = async () => {
    setPushing(true);
    setPushResult(null);
    setLanguageResults({});

    let langs;
    try {
      const res = await api.get(`/view/details/${id}`);
      langs = res.data.langs || [];
    } catch (err) {
      setPushResult({ type: 'error', message: err.response?.data?.error || 'Failed to load languages' });
      setPushing(false);
      return;
    }

    const baseName = pushForm.templateName.trim();
    const updatedLinks = [...languageLinks];
    let failed = 0;

    for (const langData of langs) {
      const language = langData.languageName;
      const subject = langData[subjectKey] || langData.subjectLine1;
      if (!subject) {
        failed++;
        setLanguageResults(prev => ({ ...prev, [language]: { type: 'error', message: 'No subject line' } }));
        continue;
      }

      setLanguageResults(prev => ({ ...prev, [language]: { type: 'pending', message: 'Pushing...' } }));
      try {
        const res = await api.post('/api/integrations/templates/push', {
          creativeId: id,
          language,
          brandId: selectedBrand._id,
          templateName: `${baseName} - ${language.toUpperCase()}`,
          subject,
          html: buildHTML(langData, language),
          target: 'linked',
          options: pushOptions
        });
        const link = res.data.link;
        const index = updatedLinks.findIndex(l => l.language === language);
        if (index >= 0) updatedLinks[index] = link; else updatedLinks.push(link);
        if (language === lang) setCrmLink(link);
        setLanguageResults(prev => ({
          ...prev,
          [language]: { type: 'success', message: `#${link.externalId} ${res.data.action}` }
        }));
      } catch (err) {
        failed++;
        setLanguageResults(prev => ({
          ...prev,
          [language]: { type: 'error', message: err.response?.data?.error || 'Failed' }
        }));
      }
    }

    setLanguageLinks(updatedLinks);
    setPushResult(failed === 0
      ? { type: 'success', message: `All ${langs.length} languages pushed to ${activeCrm.name}` }
      : { type: 'error', message: `${failed} of ${langs.length} languages failed` });
    setPushing(false);
  };

  const togglePushAllLanguages = (checked) => {
    setPushAllLanguages(checked);
    setLanguageResults({});
    setPushResult(null);
    setPushForm(prev => ({
      ...prev,
      target: 'linked',
      externalId: '',
      templateName: checked
        ? allLanguagesBaseName
        : (crmLink?.templateName || `${creativeData.title || 'Creative'} - ${lang.toUpperCase()} - ${selectedBrand.brandName}`)
    }));
  };

  const handlePushToCRM = async () => {
    setPushing(true);
    setPushResult(null);
    try {
      const res = await api.post('/api/integrations/templates/push', {
        creativeId: id,
        language: lang,
        brandId: selectedBrand._id,
        templateName: pushForm.templateName.trim(),
        subject: pushForm.subject.trim(),
        html: buildCurrentHTML(),
        target: pushForm.target,
        externalId: pushForm.externalId,
        options: pushOptions
      });
      setCrmLink(res.data.link);
      setPushForm(prev => ({ ...prev, target: 'linked', externalId: '' }));
      setPushResult({ type: 'success', message: res.data.message });
    } catch (err) {
      setPushResult({ type: 'error', message: err.response?.data?.error || 'Failed to push template' });
    } finally {
      setPushing(false);
    }
  };

  const getTemplateVersion = (
    version,
    lang,
    title,
    headline,
    body,
    bodyBtnName,
    bodyBtnLink,
    subjectLine1,
    subjectLine2,
    subjectLine3,
    preHeader,
    bodyImage,
    bodyImageLink,
    additionalText,
    additionalTextUnderBTN,
    brandName,
    disclaimer_marketing,
    viewInBrowserURL,
    frameColor,
    logoLink,
    backgroundColor,
    buttonGenralColor,
    buttonTextColor,
    logo,
    smalldisclaimer,
    borderRadius
  ) => {
    // Check if it's a custom template
    if (version.startsWith('custom-')) {
      const templateId = version.replace('custom-', '');
      const template = customTemplates.find(t => t._id === templateId);
      
      if (template && creativeData.creative === 'Email') {
        return generateHTMLFromTemplate(
          template.structure,
          {
            frameColor,
            backgroundColor,
            buttonGenralColor,
            buttonTextColor,
            logo,
            logoLink,
            disclaimer_marketing,
            smalldisclaimer,
            viewInBrowserURL,
            borderRadius,
            brandName,
            UNSUBSCRIBE_URL: selectedBrand?.UNSUBSCRIBE_URL
          },
          {
            langs: [{
              languageName: lang,
              preHeader,
              subjectLine1,
              headline,
              body,
              bodyBtnName,
              bodyBtnLink,
              bodyImage,
              bodyImageLink,
              additionalText,
              additionalTextUnderBTN
            }]
          },
          lang
        );
      }
    }

    if (creativeData.creative === 'Popup') {
      return htmlPopupTemplates[version](
        lang,
        title,
        headline,
        body,
        bodyBtnName,
        bodyBtnLink,
        brandName,
        logoLink,
        disclaimer_marketing,
        viewInBrowserURL,
        frameColor,
        backgroundColor,
        buttonGenralColor,
        buttonTextColor,
        logo,
        smalldisclaimer,
        borderRadius
      );
    } else if (creativeData.creative === 'Email') {
      return htmlEmailTemplates[version](
        lang,
        title,
        headline,
        body,
        bodyBtnName,
        bodyBtnLink,
        subjectLine1,
        subjectLine2,
        subjectLine3,
        preHeader,
        bodyImage,
        bodyImageLink,
        additionalText,
        additionalTextUnderBTN,
        brandName,
        disclaimer_marketing,
        viewInBrowserURL,
        frameColor,
        logoLink,
        backgroundColor,
        buttonGenralColor,
        buttonTextColor,
        logo,
        smalldisclaimer,
        borderRadius
      );
    } else if (creativeData.creative === 'Sms') {
      return '<p> ' + body + '</p>';
    } else if (creativeData.creative === 'Push-Notification') {
      return '<p>Title: ' + headline + '</p><p>Body: ' + body + '</p>';
    }
    return '';
  };

  // Mobile preview components
  const renderSMSPreview = () => {
    return (
      <div className="mobile-preview-container">
        <div className="mobile-frame sms-frame">
          <div className="mobile-header">
            <div className="signal-bars"></div>
            <div className="time">12:34</div>
            <div className="battery">100%</div>
          </div>
          <div className="sms-conversation">
            <div className="sms-bubble received">
              <div className="sms-text">{creativeData.content.body || 'SMS Body'}</div>
              <div className="sms-time">12:34</div>
            </div>
          </div>
        </div>
        <div className="sms-stats">
          {(() => {
            const stats = calculateSMSStats(creativeData.content.body || '');
            return (
              <div className="text-counter">
                <div className="counter-row">
                  <span className="counter-section">Total Count: {stats.totalCount}</span>
                </div>
                <div className="counter-row">
                  <span className="counter-section">Total SMS: {stats.totalSMS}</span>
                </div>
                <div className="counter-row">
                  <span className="counter-section">GSM Characters: {stats.gsm}</span>
                </div>
                <div className="counter-row total">
                  <span className="counter-section"><strong>Next Part: {stats.nextPart} characters remaining</strong></span>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    );
  };

  const renderPushNotificationPreview = () => {
    return (
      <div className="mobile-preview-container">
        <div className="mobile-frame push-frame">
          <div className="mobile-header">
            <div className="signal-bars"></div>
            <div className="time">12:34</div>
            <div className="battery">100%</div>
          </div>
          <div className="push-notification">
            <div className="notification-content">
              <div className="app-icon">📱</div>
              <div className="notification-text">
                <div className="notification-title">{creativeData.content.headline || 'Push Title'}</div>
                <div className="notification-body">{creativeData.content.body || 'Push Body'}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="push-stats">
          {(() => {
            const titleStats = calculateTextStats(creativeData.content.headline || '');
            const bodyStats = calculateTextStats(creativeData.content.body || '');
            const totalStats = {
              characters: titleStats.characters + bodyStats.characters,
              words: titleStats.words + bodyStats.words
            };
            return (
              <div className="text-counter">
                <div className="counter-row">
                  <span className="counter-section">Title: {titleStats.characters} chars, {titleStats.words} words</span>
                </div>
                <div className="counter-row">
                  <span className="counter-section">Body: {bodyStats.characters} chars, {bodyStats.words} words</span>
                </div>
                <div className="counter-row total">
                  <span className="counter-section"><strong>Total: {totalStats.characters} characters, {totalStats.words} words</strong></span>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    );
  };

  const renderPopupPreview = () => {
    return (
      <div className="mobile-preview-container">
        <div className="mobile-frame popup-frame">
          <div className="mobile-header">
            <div className="signal-bars"></div>
            <div className="time">12:34</div>
            <div className="battery">100%</div>
          </div>
          <div className="popup-overlay">
            <div className="popup-content">
              <button className="popup-close">×</button>
              <div className="popup-title">{creativeData.content.headline || 'Popup Title'}</div>
              <div 
                className="popup-body" 
                dangerouslySetInnerHTML={{ __html: creativeData.content.body || 'Popup Body' }}
              ></div>
              {creativeData.content.bodyBtnName && (
                <button className="popup-button">{creativeData.content.bodyBtnName}</button>
              )}
            </div>
          </div>
        </div>
        <div className="popup-stats">
          {(() => {
            const titleStats = calculateTextStats(creativeData.content.headline || '');
            const bodyStats = calculateTextStats(creativeData.content.body || '');
            const totalStats = {
              characters: titleStats.characters + bodyStats.characters,
              words: titleStats.words + bodyStats.words
            };
            return (
              <div className="text-counter">
                <div className="counter-row">
                  <span className="counter-section">Headline: {titleStats.characters} chars, {titleStats.words} words</span>
                </div>
                <div className="counter-row">
                  <span className="counter-section">Body: {bodyStats.characters} chars, {bodyStats.words} words</span>
                </div>
                <div className="counter-row total">
                  <span className="counter-section"><strong>Total: {totalStats.characters} characters, {totalStats.words} words</strong></span>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="creative-view-container">
        <div className="header-section">
          <motion.button 
            className="back-button"
            onClick={() => navigate(-1)}
            whileHover={{ scale: 1.05 }}
          >
            <IoArrowBack /> Back
          </motion.button>
          <h1 className="page-title">View Creative</h1>
        </div>
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading creative data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="creative-view-container">
        <div className="header-section">
          <motion.button 
            className="back-button"
            onClick={() => navigate(-1)}
            whileHover={{ scale: 1.05 }}
          >
            <IoArrowBack /> Back
          </motion.button>
          <h1 className="page-title">View Creative</h1>
        </div>
        <div className="error-message">
          <h3>Error loading creative</h3>
          <p>{error}</p>
          <motion.button 
            className="retry-button"
            onClick={() => window.location.reload()}
            whileHover={{ scale: 1.03 }}
          >
            Try Again
          </motion.button>
        </div>
      </div>
    );
  }

  if (!creativeData) {
    return (
      <div className="creative-view-container">
        <div className="header-section">
          <motion.button 
            className="back-button"
            onClick={() => navigate(-1)}
            whileHover={{ scale: 1.05 }}
          >
            <IoArrowBack /> Back
          </motion.button>
          <h1 className="page-title">View Creative</h1>
        </div>
        <div className="no-data-message">
          <h3>No creative data available</h3>
          <p>The requested creative could not be loaded.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="creative-layout">
      {/* Header Section */}
      <div className="creative-header">
        <motion.button 
          className="back-button"
          onClick={() => navigate(-1)}
          whileHover={{ scale: 1.05 }}
        >
          <IoArrowBack /> Back
        </motion.button>
        <h1 className="page-title">{creativeData.title}</h1>
        <div className="header-actions">
          {(creativeData?.creative === 'Email' || creativeData?.creative === 'Popup') &&
           canCopyHTML && (
            <motion.button
              className="action-button copy-button"
              onClick={handleCopyHTML}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaCopy /> Copy HTML
            </motion.button>
          )}
          {creativeData?.creative === 'Email' && canPushToCRM && activeCrm && (
            <motion.button
              className="action-button crm-push-button"
              onClick={openPushModal}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaCloudUploadAlt /> Push to {activeCrm.name}
            </motion.button>
          )}
          {canExtractContent && (
            <motion.button
              className="action-button extract-button"
              onClick={handleExtract}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaDownload /> Extract
            </motion.button>
          )}
          {getCreativePermissions(creativeData?.creative).canEdit && (
            <motion.button
              className="action-button edit-button"
              onClick={handleEdit}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaEdit /> Edit
            </motion.button>
          )}
        </div>
      </div>

      {copied && (
        <div className="copy-notification">
          HTML copied to clipboard!
        </div>
      )}

      <AnimatePresence>
        {showPushModal && activeCrm && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="modal-content crm-push-modal"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
            >
              <button className="modal-close-button" onClick={() => setShowPushModal(false)}>✕</button>
              <h3>Push to {activeCrm.name}</h3>

              {creativeData.availableLanguages.length > 1 && (
                <label className="crm-push-all-toggle">
                  <input
                    type="checkbox"
                    checked={pushAllLanguages}
                    disabled={pushing}
                    onChange={(e) => togglePushAllLanguages(e.target.checked)}
                  />
                  All languages ({creativeData.availableLanguages.length}) — one template per language
                </label>
              )}

              {pushAllLanguages ? (
                <table className="crm-push-languages">
                  <tbody>
                    {creativeData.availableLanguages.map(language => {
                      const link = languageLinks.find(l => l.language === language);
                      const result = languageResults[language];
                      return (
                        <tr key={language}>
                          <td className="crm-push-lang">{language.toUpperCase()}</td>
                          <td>{link ? `Update #${link.externalId}` : 'Create new'}</td>
                          <td className={`crm-push-status ${result?.type || ''}`}>{result?.message || ''}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
              <div className="crm-push-link-info">
                {crmLink ? (
                  <>
                    Linked to template <strong>#{crmLink.externalId}</strong>
                    {crmLink.lastSyncedAt && <> — last pushed {new Date(crmLink.lastSyncedAt).toLocaleString()}{crmLink.lastSyncedBy ? ` by ${crmLink.lastSyncedBy}` : ''}</>}
                  </>
                ) : (
                  <>Not pushed yet for <strong>{lang.toUpperCase()}</strong> / <strong>{selectedBrand?.brandName}</strong>.</>
                )}
              </div>
              )}

              {!pushAllLanguages && (
              <div className="integration-field">
                <label>Action</label>
                <div className="crm-push-targets">
                  <label>
                    <input type="radio" checked={pushForm.target === 'linked'} onChange={() => handlePushTargetChange('linked')} />
                    {crmLink ? `Update linked template #${crmLink.externalId}` : 'Create new template'}
                  </label>
                  {crmLink && (
                    <label>
                      <input type="radio" checked={pushForm.target === 'new'} onChange={() => handlePushTargetChange('new')} />
                      Create a new template instead
                    </label>
                  )}
                  <label>
                    <input type="radio" checked={pushForm.target === 'existing'} onChange={() => handlePushTargetChange('existing')} />
                    Update another existing {activeCrm.name} template
                  </label>
                </div>
              </div>
              )}

              {pushForm.target === 'existing' && (
                <div className="integration-field">
                  <label>Template</label>
                  <select
                    className="integration-input"
                    value={pushForm.externalId}
                    onChange={(e) => setPushForm(prev => ({ ...prev, externalId: e.target.value }))}
                  >
                    <option value="">— Select template —</option>
                    {crmTemplates.map(t => (
                      <option key={t.id} value={t.id}>{t.name} (#{t.id})</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="integration-field">
                <label>Template Name</label>
                <input
                  className="integration-input"
                  value={pushForm.templateName}
                  onChange={(e) => setPushForm(prev => ({ ...prev, templateName: e.target.value }))}
                />
                {pushAllLanguages && (
                  <small className="integration-help">
                    The language is appended, e.g. "{pushForm.templateName || 'Name'} - {lang.toUpperCase()}"
                  </small>
                )}
              </div>

              {pushAllLanguages ? (
              <div className="integration-field">
                <label>Subject</label>
                <select className="integration-input" value={subjectKey} onChange={(e) => setSubjectKey(e.target.value)}>
                  <option value="subjectLine1">Subject Line 1</option>
                  <option value="subjectLine2">Subject Line 2</option>
                  <option value="subjectLine3">Subject Line 3</option>
                </select>
                <small className="integration-help">
                  Each language uses its own translation. Falls back to Subject Line 1 if empty.
                </small>
              </div>
              ) : (
              <div className="integration-field">
                <label>Subject</label>
                <input
                  className="integration-input"
                  list="crm-subject-options"
                  value={pushForm.subject}
                  onChange={(e) => setPushForm(prev => ({ ...prev, subject: e.target.value }))}
                />
                <datalist id="crm-subject-options">
                  {subjectOptions.map(subject => <option key={subject} value={subject} />)}
                </datalist>
              </div>
              )}

              {activeCrm.pushFields.length > 0 && (
                <div className="crm-push-fields">
                  {activeCrm.pushFields.map(field => (
                    <div className="integration-field" key={field.key}>
                      <label>
                        {field.label} {field.required && <span className="required">*</span>}
                      </label>
                      {renderPushField(field)}
                    </div>
                  ))}
                </div>
              )}

              <small className="integration-help">
                {pushAllLanguages
                  ? 'The HTML of the selected brand and template version will be generated for every language.'
                  : 'The HTML of the currently selected brand, language and template version will be sent.'}
              </small>

              {pushResult && (
                <div className={`integration-badge ${pushResult.type === 'success' ? 'ok' : 'failed'}`} style={{ marginTop: '1rem', whiteSpace: 'normal' }}>
                  {pushResult.message}
                </div>
              )}

              <div className="modal-actions">
                <button className="btn-cancel" onClick={() => setShowPushModal(false)}>Close</button>
                <button
                  className="btn-save"
                  onClick={pushAllLanguages ? handlePushAllLanguages : handlePushToCRM}
                  disabled={
                    pushing || missingPushFields.length > 0 || !pushForm.templateName.trim() ||
                    (!pushAllLanguages && (!pushForm.subject.trim() || (pushForm.target === 'existing' && !pushForm.externalId)))
                  }
                >
                  <FaCloudUploadAlt />&nbsp;{pushing ? 'Pushing...' : pushAllLanguages ? `Push ${creativeData.availableLanguages.length} languages` : 'Push'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="creative-main-wrapper">
      {/* Sidebar */}
      <aside className="creative-sidebar">
        {creativeData.creative === 'Email' && (
            <div className="control-section">
              <h3>Brand</h3>
              <div className="brand-selector">
                {brands.map((brand) => (
                  <motion.div
                    key={brand._id}
                    className={`brand-option ${brand.brandName === selectedBrand?.brandName ? 'selected' : ''}`}
                    onClick={() => handleBrandChange(brand)}
                    whileHover={{ scale: 1.02 }}
                  >
                    {brand.brandName}
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          <div className="control-section">
            <h3>Language</h3>
            <div className="language-selector">
              {creativeData.availableLanguages.map((language) => (
                <motion.div
                  key={language}
                  className={`language-option ${language === lang ? 'selected' : ''}`}
                  onClick={() => handleLanguageChange(language)}
                  whileHover={{ scale: 1.02 }}
                >
                  {language.toUpperCase()}
                </motion.div>
              ))}
            </div>
          </div>

          {creativeData.creative !== 'Sms' && creativeData.creative !== 'Push-Notification' && (
            <div className="control-section">
              <h3>Template Version</h3>
              <div className="version-selector">
                {creativeData.creative === 'Popup' &&
                  Object.keys(htmlPopupTemplates).map((version) => (
                    <motion.div
                      key={version}
                      className={`version-option ${selectedVersion === version ? 'selected' : ''}`}
                      onClick={() => handleVersionChange(version)}
                      whileHover={{ scale: 1.02 }}
                    >
                      {version.replace('version', 'Version ')}
                    </motion.div>
                  ))}
                {creativeData.creative === 'Email' && (
                  <>
                    {Object.keys(htmlEmailTemplates).map((version) => (
                      <motion.div
                        key={version}
                        className={`version-option ${selectedVersion === version ? 'selected' : ''}`}
                        onClick={() => handleVersionChange(version)}
                        whileHover={{ scale: 1.02 }}
                      >
                        {version.replace('version', 'Version ')}
                      </motion.div>
                    ))}
                    {customTemplates && customTemplates.length > 0 && customTemplates.map((template) => (
                      <motion.div
                        key={`custom-${template._id}`}
                        className={`version-option custom-template ${selectedVersion === `custom-${template._id}` ? 'selected' : ''}`}
                        onClick={() => handleVersionChange(`custom-${template._id}`)}
                        whileHover={{ scale: 1.02 }}
                      >
                        📧 {template.name}
                      </motion.div>
                    ))}
                  </>
                )}
              </div>
            </div>
          )}

        </aside>

      {/* Main Content Area */}
      <div className="creative-main-content">
        {/* Main Content Area */}
        <main className="creative-content">
          {/* Preview Panel */}
          <div className="preview-panel">
            <div className="preview-header">
              <h3>Creative Preview</h3>
            </div>
            
            <div className="preview-container">
              {creativeData.creative === 'Email' ? (
                selectedBrand ? (
                  <div 
                    className="preview-content"
                    dangerouslySetInnerHTML={{
                      __html: getTemplateVersion(
                        selectedVersion,
                        lang,
                        creativeData.title || '',
                        creativeData.content.headline || '',
                        creativeData.content.body || '',
                        creativeData.content.bodyBtnName || '',
                        creativeData.content.bodyBtnLink || '',
                        creativeData.content.subjectLine1 || '',
                        creativeData.content.subjectLine2 || '',
                        creativeData.content.subjectLine3 || '',
                        creativeData.content.preHeader || '',
                        creativeData.content.bodyImage || '',
                        creativeData.content.bodyImageLink || '',
                        creativeData.content.additionalText || '',
                        creativeData.content.additionalTextUnderBTN || '',
                        selectedBrand.brandName,
                        selectedBrand.disclaimer_marketing,
                        selectedBrand.viewInBrowserURL,
                        selectedBrand.frameColor,
                        getLogoLinkForLanguage(lang),
                        selectedBrand.backgroundColor,
                        selectedBrand.buttonGenralColor,
                        selectedBrand.buttonTextColor,
                        selectedBrand.logo,
                        selectedBrand.smalldisclaimer,
                        selectedBrand.borderRadius
                      ),
                    }}
                  />
                ) : (
                  <div className="no-brand-selected">
                    Please select a brand to preview the creative
                  </div>
                )
              ) : creativeData.creative === 'Sms' ? (
                renderSMSPreview()
              ) : creativeData.creative === 'Push-Notification' ? (
                renderPushNotificationPreview()
              ) : creativeData.creative === 'Popup' ? (
                renderPopupPreview()
              ) : null}
            </div>
          </div>
        </main>
      </div>
      </div>
    </div>
  );
};

export default ViewCreative;